import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

/* ───────────────────────── types ───────────────────────── */
interface Outcome {
  title: string;
  icon: React.ReactNode;
}

interface Phase {
  number: number;
  title: string;
  levels: string;
  duration: string;
  activities: string;
  description: string;
  color: string;
  icon: React.ReactNode;
}

interface MethodStep {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface FAQ {
  question: string;
  answer: string;
}

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

/* ───────────────────────── animation variants ───────────────────────── */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.12 },
  }),
};

/* ────────────────────────── data ────────────────────────── */
const outcomes: Outcome[] = [
  {
    title: 'Votre enfant saura résoudre les conflits',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path
          d="M14 30c-3.3 0-6-2.7-6-6s2.7-6 6-6h2l4-4 4 4h2c3.3 0 6 2.7 6 6s-2.7 6-6 6"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10 34l4-4m24-12l-4 4"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M17 38h14M24 30v8"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="18" cy="22" r="1.5" fill="currentColor" />
        <circle cx="30" cy="22" r="1.5" fill="currentColor" />
        <path
          d="M20 27s2 2 4 2 4-2 4-2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: 'Votre enfant gérera ses émotions avec confiance',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path
          d="M24 42s-16-9.2-16-20c0-5.5 4-10 9-10 3.2 0 5.9 1.7 7 4.2 1.1-2.5 3.8-4.2 7-4.2 5 0 9 4.5 9 10 0 10.8-16 20-16 20z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M18 20v6l3 3 3-3v-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M30 20l-3 4h6l-3 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    title: 'Votre enfant travaillera efficacement en équipe',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="24" cy="14" r="5" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="12" cy="18" r="4" stroke="currentColor" strokeWidth="2" />
        <circle cx="36" cy="18" r="4" stroke="currentColor" strokeWidth="2" />
        <path
          d="M18 32c0-3.3 2.7-6 6-6s6 2.7 6 6"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M8 34c0-2.2 1.8-4 4-4h2M38 34c-0-2.2-1.8-4-4-4h-2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M16 38h16"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: 'Votre enfant développera son leadership naturel',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path
          d="M24 6l5.5 11 12.5 2-9 8.5 2 12.5L24 34l-11 6 2-12.5-9-8.5 12.5-2z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <circle cx="24" cy="22" r="5" stroke="currentColor" strokeWidth="2" />
        <path
          d="M24 17v-3M24 27v3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const phases: Phase[] = [
  {
    number: 1,
    title: 'Fondations',
    levels: 'CP-CE1',
    duration: '2 ans',
    activities: '30 activités',
    description:
      "Découverte de l'écoute active, la gestion des émotions, l'empathie et la coopération",
    color: 'from-teal-400 to-teal-500',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <rect x="6" y="16" width="6" height="10" rx="1.5" stroke="currentColor" strokeWidth="2" />
        <rect x="13" y="10" width="6" height="16" rx="1.5" stroke="currentColor" strokeWidth="2" />
        <rect x="20" y="6" width="6" height="20" rx="1.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    number: 2,
    title: 'Développement',
    levels: 'CE2-CM1',
    duration: '2 ans',
    activities: '30 activités',
    description:
      "Approfondissement des compétences et développement de l'autonomie émotionnelle",
    color: 'from-teal-500 to-teal-600',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M16 4v24M8 12l8-8 8 8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6 20h20"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    number: 3,
    title: 'Leadership',
    levels: 'CM2-CE6',
    duration: '1 an',
    activities: '30 activités',
    description:
      "Maîtrise et transmission : les élèves deviennent mentors",
    color: 'from-teal-600 to-emerald-600',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M16 4l3.5 7 7.5 1-5.5 5 1.5 7.5L16 20.5l-7 4 1.5-7.5-5.5-5 7.5-1z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const methodSteps: MethodStep[] = [
  {
    number: 1,
    title: 'Découverte',
    description:
      "L'enfant explore une nouvelle compétence à travers une mise en situation",
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="2" />
        <path
          d="M16 10v6l4 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="16" cy="16" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    number: 2,
    title: 'Pratique',
    description:
      "Des activités ludiques pour s'exercer et intégrer la compétence",
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M6 26V6h20v20H6z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M10 18l4-4 4 4 4-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    number: 3,
    title: 'Application',
    description:
      "L'enfant applique ses acquis dans des situations réelles",
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M8 16l6 6L26 10"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    number: 4,
    title: 'Évaluation',
    description:
      "Un bilan positif pour célébrer les progrès et identifier les axes d'amélioration",
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M16 4l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const faqs: FAQ[] = [
  {
    question: "En quoi est-ce différent de ce qui se fait déjà à l'école ?",
    answer:
      "Notre programme Soft Skills est structuré sur 6 ans avec 90 activités progressives. Contrairement aux initiatives ponctuelles, chaque activité s'inscrit dans un parcours cohérent qui développe méthodiquement 4 compétences clés.",
  },
  {
    question: "Est-ce que ça prend du temps sur les matières académiques ?",
    answer:
      "Les séances Soft Skills sont intégrées dans l'emploi du temps sans réduire les heures d'enseignement. Au contraire, les compétences développées (concentration, coopération, gestion des émotions) améliorent les résultats académiques.",
  },
  {
    question: "Comment puis-je soutenir le programme à la maison ?",
    answer:
      "Chaque compétence travaillée en classe peut être renforcée à la maison par des gestes simples : écouter activement votre enfant, nommer les émotions ensemble, encourager le travail d'équipe en famille.",
  },
  {
    question: "Mon enfant est déjà sociable, est-ce utile ?",
    answer:
      "Absolument. Les Soft Skills vont bien au-delà de la sociabilité. Le programme développe le leadership, la gestion des conflits, l'intelligence émotionnelle et la prise de décision — des compétences essentielles pour la vie.",
  },
];

/* ─────────────────── FAQ accordion item ─────────────────── */
const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => (
  <motion.div
    className="border border-teal-100 rounded-2xl overflow-hidden bg-white"
    initial={false}
    animate={{ backgroundColor: isOpen ? '#f0fdfa' : '#ffffff' }}
    transition={{ duration: 0.3 }}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded-2xl"
    >
      <span className="text-base sm:text-lg font-semibold text-gray-900 pr-4">
        {question}
      </span>
      <motion.span
        className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600"
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </motion.span>
    </button>

    <motion.div
      initial={false}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
      }}
      transition={{ height: { duration: 0.35 }, opacity: { duration: 0.25 } }}
      className="overflow-hidden"
    >
      <div className="px-6 pb-5 text-gray-600 leading-relaxed">{answer}</div>
    </motion.div>
  </motion.div>
);

/* ════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ════════════════════════════════════════════════════════════ */
const SoftSkillsPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  /* parallax refs */
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* ═══════════════════ 1. HERO ═══════════════════ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-600 min-h-[92vh] flex items-center"
      >
        {/* Decorative elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] bg-emerald-400/10 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-teal-300/10 rounded-full blur-2xl" />
          {/* Subtle dot pattern */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'radial-gradient(circle, #fff 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28"
        >
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text column */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Badge */}
              <motion.div variants={itemVariants} className="mb-6">
                <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-5 py-2 rounded-full text-sm font-semibold tracking-wide border border-white/20">
                  <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />
                  Programme Soft Skills
                </span>
              </motion.div>

              {/* Age badge */}
              <motion.div variants={itemVariants} className="mb-4">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase border border-white/20">
                  CP à CE6
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] tracking-tight mb-6"
              >
                Les compétences{' '}
                <span className="relative inline-block">
                  <span className="relative z-10">qui préparent</span>
                  <span className="absolute -bottom-1 left-0 w-full h-3 bg-emerald-300/30 rounded-full" />
                </span>{' '}
                l’avenir
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                variants={itemVariants}
                className="text-lg sm:text-xl text-teal-50/90 max-w-lg leading-relaxed mb-10"
              >
                Un programme de développement socio-émotionnel
                structuré pour le primaire
              </motion.p>

              {/* Stats */}
              <motion.div
                variants={itemVariants}
                className="flex flex-wrap gap-6"
              >
                {[
                  { value: '6 ans', label: 'de parcours' },
                  { value: '90', label: 'activités' },
                  { value: '4', label: 'compétences clés' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/10"
                  >
                    <div className="text-2xl font-bold text-white">
                      {stat.value}
                    </div>
                    <div className="text-sm text-teal-100">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Icon column */}
            <motion.div
              className="hidden lg:flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            >
              <div className="relative">
                {/* Glow ring */}
                <div className="absolute inset-0 bg-white/10 rounded-full blur-2xl scale-110" />
                {/* Main icon container */}
                <div className="relative w-72 h-72 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                  <svg
                    viewBox="0 0 120 120"
                    fill="none"
                    className="w-36 h-36 text-white"
                  >
                    {/* Heart shape */}
                    <path
                      d="M60 100S20 76 20 48c0-11 8-20 18-20 6.5 0 12 3.4 14 8.4a19.4 19.4 0 0114-8.4c10 0 18 9 18 20 0 28-24 52-24 52z"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="currentColor"
                      fillOpacity="0.15"
                      strokeLinejoin="round"
                    />
                    {/* People */}
                    <circle
                      cx="46"
                      cy="56"
                      r="5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    />
                    <circle
                      cx="74"
                      cy="56"
                      r="5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    />
                    <path
                      d="M38 72c0-4.4 3.6-8 8-8h0c4.4 0 8 3.6 8 8M66 72c0-4.4 3.6-8 8-8h0c4.4 0 8 3.6 8 8"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M54 68h12"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                {/* Orbiting accent dots */}
                <motion.div
                  className="absolute -top-4 right-8 w-6 h-6 bg-emerald-300/50 rounded-full"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                  className="absolute bottom-8 -left-4 w-4 h-4 bg-teal-200/50 rounded-full"
                  animate={{ y: [0, 10, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.5,
                  }}
                />
                <motion.div
                  className="absolute top-1/2 -right-6 w-3 h-3 bg-white/40 rounded-full"
                  animate={{ x: [0, 8, 0] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1,
                  }}
                />
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Curved bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 100"
            fill="none"
            preserveAspectRatio="none"
            className="w-full h-auto block"
          >
            <path
              d="M0 40C240 80 480 100 720 80S1200 20 1440 60V100H0V40Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ═══════════════════ 2. OUTCOMES ═══════════════════ */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-teal-600 font-semibold tracking-wide text-sm uppercase mb-3">
              Résultats concrets
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Ce que votre enfant développera
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {outcomes.map((outcome, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="group relative bg-gradient-to-b from-teal-50 to-white border border-teal-100 rounded-2xl p-6 sm:p-8 text-center"
              >
                {/* Hover glow */}
                <div className="absolute inset-0 rounded-2xl bg-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative">
                  <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center group-hover:bg-teal-500 group-hover:text-white transition-colors duration-300">
                    {outcome.icon}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                    {outcome.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ 3. PROGRAM STRUCTURE ═══════════════════ */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-teal-600 font-semibold tracking-wide text-sm uppercase mb-3">
              Progression
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Un parcours de 6 ans
            </h2>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            {/* Connecting line — desktop */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 z-0">
              <div className="h-full bg-gradient-to-r from-teal-300 via-teal-400 to-emerald-500 rounded-full" />
            </div>
            {/* Connecting line — mobile */}
            <div className="lg:hidden absolute top-0 bottom-0 left-8 w-1 z-0">
              <div className="h-full bg-gradient-to-b from-teal-300 via-teal-400 to-emerald-500 rounded-full" />
            </div>

            <div className="relative z-10 grid lg:grid-cols-3 gap-10 lg:gap-8">
              {phases.map((phase, i) => (
                <motion.div
                  key={phase.number}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  className="relative flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0"
                >
                  {/* Phase circle */}
                  <motion.div
                    className={`flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br ${phase.color} text-white flex items-center justify-center shadow-lg shadow-teal-500/20`}
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {phase.icon}
                  </motion.div>

                  {/* Card */}
                  <div className="lg:mt-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 w-full">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider">
                        Phase {phase.number}
                      </span>
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs font-medium text-gray-500">
                        {phase.levels}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {phase.title}
                    </h3>

                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full font-medium">
                        {phase.duration}
                      </span>
                      <span className="text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full font-medium">
                        {phase.activities}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      {phase.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 4. METHODOLOGY ═══════════════════ */}
      <section className="py-20 sm:py-28 bg-white overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-teal-600 font-semibold tracking-wide text-sm uppercase mb-3">
              Méthodologie
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Comment ça marche
            </h2>
          </motion.div>

          {/* Steps */}
          <div className="relative max-w-5xl mx-auto">
            {/* Desktop connector line */}
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-teal-200 via-teal-300 to-teal-200 z-0" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10">
              {methodSteps.map((step, i) => (
                <motion.div
                  key={step.number}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  className="relative flex flex-col items-center text-center"
                >
                  {/* Step circle */}
                  <motion.div
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center mb-5 border-4 border-white shadow-md"
                    whileHover={{ scale: 1.08, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="text-teal-600">{step.icon}</div>
                  </motion.div>

                  {/* Number badge */}
                  <span className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                    {step.number}
                  </span>

                  {/* Arrow connector — desktop only, skip last */}
                  {i < methodSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 -right-3 text-teal-300">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        className="w-6 h-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </div>
                  )}

                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed max-w-[250px]">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ 5. FAQ ═══════════════════ */}
      <section className="py-20 sm:py-28 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-teal-600 font-semibold tracking-wide text-sm uppercase mb-3">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              Questions fréquentes
            </h2>
          </motion.div>

          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {faqs.map((faq, i) => (
              <motion.div key={i} variants={itemVariants}>
                <FAQItem
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === i}
                  onToggle={() => setOpenFAQ(openFAQ === i ? null : i)}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ 6. CTA ═══════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 py-20 sm:py-28">
        {/* Decorative */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'radial-gradient(circle, #fff 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              Découvrez le Groupe Scolaire Sanaa
            </h2>
            <p className="text-lg text-blue-100/90 max-w-xl mx-auto mb-10 leading-relaxed">
              Offrez à votre enfant un parcours éducatif qui va
              au-delà des matières académiques.
            </p>

            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-blue-600 font-bold text-lg px-8 py-4 rounded-full shadow-xl shadow-blue-900/20 hover:shadow-2xl hover:scale-105 transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
            >
              Nous contacter
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-5 h-5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default SoftSkillsPage;
